import { PresenceMonth, PresenceNow } from '../types/types';
import { israelToday, shiftYm, ymOf } from './schedule';

/* Presence in the Knesset building: helpers shared by the במשכן tab, the dots on MK
   cards and the MK page. Times in the data are UTC ISO strings; everything shown is
   Israel time. */

// Collection started on this day; the Knesset's board keeps no history
export const PRESENCE_START = '2026-10-10';
export const PRESENCE_START_LABEL = 'נתונים מ-10.10.2026';
export const PRESENCE_FIRST_MONTH = ymOf(PRESENCE_START);
export const PRESENCE_SOURCE = 'מקור: לוח הנוכחות של הכנסת';

// The list counts as stale when the last poll is older than this (it polls every minute)
const STALE_MS = 10 * 60 * 1000;
// Stays longer than this are missed exits (the backend caps them the same way)
const MAX_STAY_SECONDS = 20 * 3600;

export const isPresenceStale = (p: PresenceNow | null | undefined, nowMs: number): boolean => {
  if (!p || !p.polledAt) return true;
  const t = Date.parse(p.polledAt);
  return isNaN(t) || nowMs - t > STALE_MS;
};

const israelParts = (ms: number) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const get = (type: string) => (parts.find(x => x.type === type) || { value: '' }).value;
  return { ymd: `${get('year')}-${get('month')}-${get('day')}`, hm: `${get('hour')}:${get('minute')}`, day: get('day') };
};

// "09:12" when the arrival was today (Israel time), else "9.10, 22:10"
export const sinceLabel = (iso: string): string => {
  const ms = Date.parse(iso);
  if (isNaN(ms)) return '';
  const p = israelParts(ms);
  if (p.ymd === israelToday()) return p.hm;
  return `${parseInt(p.ymd.slice(8, 10), 10)}.${parseInt(p.ymd.slice(5, 7), 10)}, ${p.hm}`;
};

// Seconds since an arrival, capped like the backend caps a missed exit
export const staySeconds = (iso: string, nowMs: number): number => {
  const ms = Date.parse(iso);
  if (isNaN(ms)) return 0;
  return Math.max(0, Math.min(MAX_STAY_SECONDS, Math.round((nowMs - ms) / 1000)));
};

// 4 ש׳ 28 דק׳ / 28 דק׳
export const durationLabel = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} דק׳`;
  return m === 0 ? `${h} ש׳` : `${h} ש׳ ${m} דק׳`;
};

// Hours as h:mm (46:20)
export const hoursLabel = (seconds: number): string => {
  const mins = Math.round(seconds / 60);
  const m = mins % 60;
  return `${Math.floor(mins / 60)}:${m < 10 ? '0' : ''}${m}`;
};

// "לפני 2 דק׳" for the last update
export const agoLabel = (iso: string | null | undefined, nowMs: number): string => {
  const ms = iso ? Date.parse(iso) : NaN;
  if (isNaN(ms)) return '';
  const mins = Math.max(0, Math.round((nowMs - ms) / 60000));
  if (mins < 1) return 'עכשיו';
  if (mins < 60) return `לפני ${mins} דק׳`;
  const h = Math.floor(mins / 60);
  if (h < 24) return h === 1 ? 'לפני שעה' : `לפני ${h} שעות`;
  return `ב-${sinceLabel(iso as string)}`;
};

const MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
export const monthLabel = (ym: string): string => `${MONTHS[parseInt(ym.slice(5, 7), 10) - 1]} ${ym.slice(0, 4)}`;

// Midnight Israel time on the 1st of the month, as epoch ms (the offset is +2 or +3)
const israelMonthStart = (ym: string): number => {
  const utcMidnight = Date.parse(`${ym}-01T00:00:00Z`);
  const offsetHours = parseInt(israelParts(utcMidnight).hm.slice(0, 2), 10) || 0;
  return utcMidnight - offsetHours * 3600 * 1000;
};

export interface PresenceTotals {
  seconds: number;
  days: number;
  visits: number;
  byDay: Record<string, number>; // 'DD' → seconds
}

/* One person's month: the credited stays plus, in the current month, the stay still in
   progress (credited by the backend only on exit). The live part counts from the later of
   the arrival and the 1st of the month, and adds today as a day and the stay as a visit. */
export const monthTotals = (
  month: PresenceMonth | null,
  ym: string,
  now: PresenceNow | null,
  nowMs: number,
  id: string,
): PresenceTotals => {
  const byDay: Record<string, number> = {};
  const credited = month && month.days ? month.days[id] || {} : {};
  Object.keys(credited).forEach(d => { byDay[d] = credited[d]; });
  let seconds = month && month.seconds ? month.seconds[id] || 0 : 0;
  let visits = month && month.visits ? month.visits[id] || 0 : 0;

  const since = now && now.present ? now.present[id] : undefined;
  if (since && ymOf(israelToday()) === ym && !isPresenceStale(now, nowMs)) {
    const from = Math.max(Date.parse(since), israelMonthStart(ym));
    const live = staySeconds(new Date(from).toISOString(), nowMs);
    if (live > 0) {
      seconds += live;
      visits += 1;
      const today = israelParts(nowMs).day;
      byDay[today] = (byDay[today] || 0) + live;
    }
  }
  const days = Object.keys(byDay).filter(d => byDay[d] > 0).length;
  return { seconds, days, visits, byDay };
};

// Who the monthly ranking covers: sitting MKs and Norwegian Law ministers
export const isRanked = (m: { isCurrentMk?: boolean; governmentMember?: boolean; isActiveMk?: boolean | null }): boolean =>
  m.isCurrentMk === true || (!!m.governmentMember && m.isActiveMk === false);

// The ranking's IDs: those people, plus anyone credited that month (or, in the current
// month, inside now) who has no member doc yet
export const rankingIds = (memberIds: string[], month: PresenceMonth | null, now: PresenceNow | null, ym: string): string[] => {
  const ids: Record<string, boolean> = {};
  memberIds.forEach(id => { ids[id] = true; });
  if (month) Object.keys(month.seconds || {}).forEach(id => { ids[id] = true; });
  if (now && ym === ymOf(israelToday())) Object.keys(now.present || {}).forEach(id => { ids[id] = true; });
  return Object.keys(ids);
};

// Place in the month's ranking by hours (1 = most time), or 0 with no time that month
export const hoursRank = (id: string, ids: string[], month: PresenceMonth | null, ym: string, now: PresenceNow | null, nowMs: number): number => {
  const mine = monthTotals(month, ym, now, nowMs, id).seconds;
  if (mine <= 0) return 0;
  return 1 + ids.filter(x => x !== id && monthTotals(month, ym, now, nowMs, x).seconds > mine).length;
};

// The month's days that can have data, as 'DD' (October 2026 starts on the 10th)
export const presenceDays = (ym: string): string[] => {
  const last = new Date(Date.UTC(parseInt(ym.slice(0, 4), 10), parseInt(ym.slice(5, 7), 10), 0)).getUTCDate();
  const first = ym === PRESENCE_FIRST_MONTH ? parseInt(PRESENCE_START.slice(8, 10), 10) : 1;
  const out: string[] = [];
  for (let d = first; d <= last; d++) out.push(d < 10 ? `0${d}` : String(d));
  return out;
};

// Today's day of the month in Israel, as 'DD'
export const israelDay = (nowMs: number): string => israelParts(nowMs).day;

// Months that can have data: from the first month of collection to the current one
export const presenceMonths = (): string[] => {
  const last = ymOf(israelToday());
  const out: string[] = [];
  let ym = PRESENCE_FIRST_MONTH;
  while (ym <= last && out.length < 240) {
    out.push(ym);
    ym = shiftYm(ym, 1);
  }
  return out;
};
